import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { UserCircle2 } from 'lucide-react';
import { fetchApplicantDetails, updateApplicantStatus } from '../redux/ApplicantSlice';
import { Oval } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/Config';

const ApplicantDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false)

    const { selectedApplicant: applicant, loading, error } = useSelector(
        (state) => state.applicants
    );

    useEffect(() => {
        if (id) {
            dispatch(fetchApplicantDetails(id));
        }
    }, [dispatch, id]);

    const handleStatusUpdate = async (action) => {
        const status = action === 'approve' ? 'approved' : 'rejected';

        try {
            setIsUpdating(true)
            await dispatch(updateApplicantStatus({
                applicantId: applicant.id,
                status
            })).unwrap();
            setIsConfirmOpen(false);
            toast.success('Applicant status updates successful')
            // Optionally navigate back after successful update
            // navigate(-1);
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Failed to update applicant')
        } finally {
            setIsUpdating(false)
        }
    };

    const initiateAction = (action) => {
        setPendingAction(action);
        setIsConfirmOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved':
                return 'bg-green-100 text-green-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-600">Error: {error}</div>
            </div>
        );
    }

    if (!applicant) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">No applicant found</div>
            </div>
        );
    }

    return (
        <div className="mx-auto bg-white rounded-lg">
            {/* Header with back button */}
            <div className="mb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="hover:underline"
                >
                    Go back
                </button>
            </div>

            {/* Header Section */}
            <div className="p-2 sm:p-6 border-b border-gray-200">
                <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircle2 className="h-10 w-10 text-gray-400" />
                    </div>
                    <div>
                        <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                            {`${applicant.first_name} ${applicant.middle || ''} ${applicant.last_name}`}
                        </h2>
                        <p className="text-gray-500">{applicant.email}</p>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-2 sm:p-6 flex flex-col gap-12">
                {/* Status Badge */}
                <div className="flex flex-wrap gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(applicant.status)}`}>
                        Status: {applicant.status}
                    </span>
                </div>

                {/* Personal Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                        <p><span className="font-semibold">Sex:</span> {applicant.sex}</p>
                        <p><span className="font-semibold">Date of Birth:</span> {formatDate(applicant.date_of_birth)}</p>
                        <p><span className="font-semibold">Secondary Email:</span> {applicant.secondary_email}</p>
                        <p><span className="font-semibold">Primary Phone:</span> {applicant.primary_phone}</p>
                        <p><span className="font-semibold">Secondary Phone:</span> {applicant.secondary_phone}</p>
                    </div>
                </div>

                {/* Employment Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Previous Employment Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                        <p><span className="font-semibold">Place of Work:</span> {applicant.place_of_work}</p>
                        <p><span className="font-semibold">Year of Employment:</span> {applicant.year_of_employment}</p>
                    </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                        <p><span className="font-semibold">Address Line 1:</span> {applicant.address_line_1}</p>
                        <p><span className="font-semibold">Address Line 2:</span> {applicant.address_line_2}</p>
                        <p><span className="font-semibold">City:</span> {applicant.city}</p>
                        <p><span className="font-semibold">Country:</span> {applicant.country}</p>
                    </div>
                </div>

                {/* FETP Courses */}
                {applicant.fetp_courses && applicant.fetp_courses.length > 0 && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-semibold">FETP Courses</h3>
                        {applicant.fetp_courses.map((course, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                    <p><span className="font-semibold">Course Name:</span> {course.name}</p>
                                    <p><span className="font-semibold">Level:</span> {course.level}</p>
                                    <p><span className="font-semibold">Cohort Number:</span> {course.cohort_number}</p>
                                    <p><span className="font-semibold">Year Completed:</span> {course.year_completed}</p>
                                    <p><span className="font-semibold">Status:</span> {course.completed ? 'Completed' : 'In Progress'}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Actions Section */}
            {applicant.status === 'pending' && (
                <div className="p-6 border-t border-gray-200 flex justify-end space-x-2">
                    <button
                        onClick={() => initiateAction('reject')}
                        className="px-4 py-2 border border-red-600 text-red-600 rounded-md hover:bg-red-50 transition-colors"
                    >
                        Reject
                    </button>
                    <button
                        onClick={() => initiateAction('approve')}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Approve
                    </button>
                </div>
            )}

            {/* Confirmation Modal */}
            {isConfirmOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold mb-2">Confirm Action</h3>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to {pendingAction} this application?
                        </p>
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setIsConfirmOpen(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleStatusUpdate(pendingAction)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                {isUpdating ? (
                                    <div className=" flex justify-center items-center">
                                        <Oval
                                            visible={true}
                                            height="20"
                                            width="20"
                                            color="#ffff"
                                            ariaLabel="oval-loading"
                                            wrapperStyle={{}}
                                            wrapperClass=""
                                        />
                                    </div>
                                ) : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicantDetail;